/**
 * Analytics Service for Umami
 * Advanced event tracking with SSR protection
 * 
 * @description Service vanilla JavaScript pour le tracking d'événements Umami.
 * Compatible avec les applications React et protégé contre les erreurs SSR.
 */

/**
 * Checks if Umami is available in the current environment
 * @returns {boolean} True if Umami is loaded and ready
 */
function isUmamiAvailable() {
    return typeof window !== 'undefined' &&
        typeof window.umami !== 'undefined' &&
        typeof window.umami.track === 'function';
}

/**
 * Track a simple event or an event with additional data
 * @param {string} eventName - Name of the event to track
 * @param {Object} [eventData] - Optional additional data for the event
 * @returns {boolean} True if the event was sent, false otherwise
 * 
 * @example
 * // Track a simple event
 * analyticsService.track('Button Click');
 * 
 * @example
 * // Track an event with data
 * analyticsService.track('File Download', { filename: 'doc.md', size: 1024 });
 */
function track(eventName, eventData) {
    if (!isUmamiAvailable()) {
        if (process.env.NODE_ENV === 'development') {
            console.warn('[Analytics] Umami not available, event not sent:', eventName);
        }
        return false;
    }

    try {
        if (eventData && typeof eventData === 'object') {
            window.umami.track(eventName, eventData);
        } else {
            window.umami.track(eventName);
        }
        return true;
    } catch (error) {
        console.error('[Analytics] Error tracking event:', error);
        return false;
    }
}

/**
 * Track a page view (useful for SPAs)
 * @param {string} [url] - Optional custom URL to track
 * @returns {boolean} True if the page view was sent, false otherwise
 */
function trackPageView(url) {
    if (!isUmamiAvailable()) {
        return false;
    }

    try {
        if (url) {
            window.umami.track({ url });
        } else {
            window.umami.track();
        }
        return true;
    } catch (error) {
        console.error('[Analytics] Error tracking page view:', error);
        return false;
    }
}

/**
 * Analytics Service object with all tracking methods
 */
export const analyticsService = {
    track,
    trackPageView,
    isUmamiAvailable
};

export default analyticsService;



/** WEBPACK FOOTER **
 ** ./src/services/analyticsService.js
 **/